import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Input } from "../../components/Input";
import { api } from "../../api/client";
import type { EmailLog, QuoteDetail, SendRequest } from "../../api/types";

// 寄送對話框。收件者 / 副本 / 主旨 / 內文與附件勾選,一律可改後才送出。
//
// 兩條規矩寫死在這裡:
// 1. 送出失敗絕不關閉對話框 —— 企劃剛打的內文不能因為一次 SMTP 逾時就消失,
//    錯誤原樣顯示在表單裡,改完可以直接重送。
// 2. 主旨與內文的預設值是 backend/app/mail/__init__.py 的鏡像。後端只有在
//    收到空字串時才會自己補範本;這裡一定會送出內容,所以兩邊要長得一樣。

/** 寄送只需要單子的這幾欄,草稿與 API 型別都塞得進來。 */
export type SendQuote = Pick<
  QuoteDetail,
  "id" | "quote_no" | "title" | "contact_email" | "event_period" | "venue" | "valid_until"
>;

/** 收件者欄位的分隔字元:逗號、頓號、分號與空白都收,信箱本身不含這些字元。 */
const SEPARATORS = /[,、;;\s]+/;

/** 把一行文字拆成信箱清單。空白與重複的分隔字元一律略過。 */
export function parseAddresses(raw: string): string[] {
  return String(raw ?? "")
    .split(SEPARATORS)
    .map((address) => address.trim())
    .filter((address) => address !== "");
}

/** 預設主旨。與後端 default_subject 一致,一定帶得出單號。 */
export function defaultSubject(quote: SendQuote): string {
  const title = quote.title?.trim() ?? "";
  const head = `【星采設計】報價單 ${quote.quote_no}`;
  return title === "" ? head : `${head} — ${title}`;
}

/**
 * 預設內文。與後端 default_body 同一套排版與同一套欄位名稱(專案名稱 /
 * 專案時間 / 專案地點),企劃可在寄出前自行修改。
 *
 * 欄位名要跟著附件走:這封信附的就是那份 PDF,內文寫一套、附件印另一套的話,
 * 客戶在同一次寄送裡會看到同一個欄位的兩種說法。
 */
export function defaultBody(quote: SendQuote): string {
  const title = quote.title?.trim() ?? "";
  const lines = ["您好:", ""];
  lines.push(
    title === ""
      ? `謹附上報價單(${quote.quote_no}),請查收。`
      : `謹附上「${title}」的報價單(${quote.quote_no}),請查收。`,
  );

  if (quote.event_period?.trim()) lines.push(`專案時間:${quote.event_period.trim()}`);
  if (quote.venue?.trim()) lines.push(`專案地點:${quote.venue.trim()}`);
  if (quote.valid_until) lines.push(`本報價有效期至 ${quote.valid_until}。`);

  lines.push("", "若內容有需要調整的地方,再請不吝告知,我們會立即修正。");
  return lines.join("\n");
}

export interface SendDialogProps {
  open: boolean;
  quote: SendQuote;
  onClose: () => void;
  /** 寄送成功。外層負責關閉對話框、提示與更新狀態徽章。 */
  onSent: (log: EmailLog) => void;
}

export function SendDialog({ open, quote, onClose, onSent }: SendDialogProps) {
  const [to, setTo] = useState(() => quote.contact_email ?? "");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(() => defaultSubject(quote));
  const [body, setBody] = useState(() => defaultBody(quote));
  const [attachPdf, setAttachPdf] = useState(true);
  const [attachXlsx, setAttachXlsx] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 每次重新開啟都以單子當下的內容重新帶入 —— 承辦人的信箱可能剛剛才改過。
  // 只看 open 與單號,不看 quote 的物件參考:編輯器每打一個字都會換一個新物件,
  // 跟著重帶會把使用者正在打的內文洗掉。
  useEffect(() => {
    if (!open) return;
    setTo(quote.contact_email ?? "");
    setCc("");
    setSubject(defaultSubject(quote));
    setBody(defaultBody(quote));
    setAttachPdf(true);
    setAttachXlsx(false);
    setError(null);
    setSending(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quote.id]);

  const recipients = parseAddresses(to);
  const canSend = recipients.length > 0 && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    const payload: SendRequest = {
      to: recipients,
      cc: parseAddresses(cc),
      subject,
      body,
      attach_pdf: attachPdf,
      attach_xlsx: attachXlsx,
    };
    try {
      const log = await api.post<EmailLog>(`/quotes/${quote.id}/send`, payload);
      onSent(log);
    } catch (err) {
      // 後端的錯誤原文最有用(SMTP 拒絕、信箱格式不對),照原樣留在對話框裡。
      setError(err instanceof Error ? err.message : "寄送失敗,請稍後再試");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="寄送報價單"
      maxWidth="max-w-xl"
      closeOnBackdrop={false}
      footer={
        <>
          <Button onClick={onClose} disabled={sending}>
            取消
          </Button>
          <Button
            variant="primary"
            icon={<Send aria-hidden="true" size={16} />}
            disabled={!canSend}
            loading={sending}
            onClick={send}
          >
            寄出
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          label="收件者"
          type="email"
          multiple
          value={to}
          hint="多位收件者請用逗號分隔"
          onChange={(event) => setTo(event.target.value)}
        />
        <Input
          label="副本"
          type="email"
          multiple
          value={cc}
          onChange={(event) => setCc(event.target.value)}
        />
        <Input label="主旨" value={subject} onChange={(event) => setSubject(event.target.value)} />

        <label className="flex min-w-0 flex-col gap-1 text-sm font-medium text-[var(--text-2)]">
          內文
          <textarea
            rows={10}
            value={body}
            onChange={(event) => setBody(event.target.value)}
            className="w-full min-w-0 resize-y rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2 py-1.5 text-sm text-[var(--text)]"
          />
        </label>

        <fieldset className="m-0 flex flex-wrap items-center gap-4 border-0 p-0">
          <legend className="mb-1 p-0 text-sm font-medium text-[var(--text-2)]">附件</legend>
          <label className="flex items-center gap-2 text-sm text-[var(--text)]">
            <input
              type="checkbox"
              checked={attachPdf}
              onChange={(event) => setAttachPdf(event.target.checked)}
              className="h-4 w-4 accent-[var(--accent)]"
            />
            附上 PDF
          </label>
          <label className="flex items-center gap-2 text-sm text-[var(--text)]">
            <input
              type="checkbox"
              checked={attachXlsx}
              onChange={(event) => setAttachXlsx(event.target.checked)}
              className="h-4 w-4 accent-[var(--accent)]"
            />
            附上 Excel
          </label>
        </fieldset>

        {recipients.length === 0 ? (
          <p className="m-0 text-xs text-[var(--text-3)]">請至少填一位收件者才能寄出。</p>
        ) : null}

        {error ? (
          <p
            role="alert"
            className="m-0 rounded-[var(--radius)] border border-[var(--danger)] px-2.5 py-2 text-sm text-[var(--danger)]"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
